// src/modules/portal/components/StateGrid.tsx
// Grid clicável de estados brasileiros

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BRAZILIAN_STATES, BrazilianState } from '../data/brazilianStates';
import { MapPin, Fish } from 'lucide-react';

interface StateGridProps {
  highlightFishingStates?: boolean;
  navigate?: (path: string) => void;
}

export const StateGrid: React.FC<StateGridProps> = ({ 
  highlightFishingStates = true,
  navigate: propNavigate 
}) => {
  const internalNavigate = useNavigate();
  const navigate = propNavigate || internalNavigate;
  
  const fishingStates = ['AM', 'PA', 'TO', 'GO', 'MT', 'MS'];
  
  const handleStateClick = (state: BrazilianState) => {
    navigate(`/estados/${state.code.toLowerCase()}`);
  };

  const groupedByRegion = BRAZILIAN_STATES.reduce((acc, state) => {
    if (!acc[state.region]) acc[state.region] = [];
    acc[state.region].push(state);
    return acc;
  }, {} as Record<string, BrazilianState[]>);

  const regions = ['Norte', 'Nordeste', 'Centro-Oeste', 'Sudeste', 'Sul'];

  return (
    <div className="space-y-20">
      {regions.map(region => (
        <div key={region} className="animate-fade-in">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <h2 className="text-sm font-black text-white/30 uppercase tracking-[0.4em] flex items-center gap-3">
              <MapPin size={16} className="text-brand-gold/40" />
              Região {region}
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
            {groupedByRegion[region]?.map(state => {
              const isFishingHotspot = fishingStates.includes(state.code);
              
              return (
                <button
                  key={state.code}
                  onClick={() => handleStateClick(state)}
                  className={`
                    relative p-6 rounded-[2.5rem] border transition-all duration-500 group overflow-hidden
                    ${isFishingHotspot && highlightFishingStates
                      ? 'bg-brand-gold/5 border-brand-gold/20 shadow-glow-gold/5'
                      : 'bg-brand-charcoal/30 border-white/5 hover:bg-brand-charcoal/50'
                    }
                    hover:scale-[1.05] hover:border-brand-gold/40
                  `}
                >
                  {/* Ray/Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Badge de destino de pesca */}
                  {isFishingHotspot && highlightFishingStates && (
                    <div className="absolute top-4 right-4 animate-pulse">
                      <Fish className="text-brand-gold" size={18} />
                    </div>
                  )}
                  
                  {/* Sigla/Emoji */}
                  <div className="text-4xl mb-4 transform group-hover:scale-110 transition-transform">
                    {state.emoji || state.code}
                  </div>
                  
                  {/* Nome do estado */}
                  <div className="text-xs font-black text-white/70 uppercase tracking-widest group-hover:text-brand-gold transition-colors">
                    {state.name}
                  </div>
                  
                  {/* Destinos populares shimmer */}
                  {state.popularDestinations && state.popularDestinations.length > 0 && (
                    <div className="mt-3 text-[9px] font-bold text-white/20 uppercase tracking-tighter group-hover:text-white/40 transition-colors line-clamp-1">
                      {state.popularDestinations.join(' • ')}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
